import { useCallback, useState } from 'react'

import { getRecording, patchSelfReport, type RecordingRecord, type SelfReport, type SelfReportProvenance } from '../../lib/api'
import { useRetryLanding } from '../../lib/useRetryLanding'
import { ApiError } from '../../lib/problem'
import {
  DRAFT_FIELDS,
  NUMERIC_FIELDS,
  QUALITY_LABELS,
  STATE_OPTIONS,
  assertWireSafe,
  buildPatch,
  draftErrors,
  draftFromReport,
  hasUnknownTags,
  provenanceWords,
  untypedRawEntries,
  type DraftField,
  type JournalQuality,
  type JournalState,
  type NumericField,
  type SelfReportDraft,
} from '../../lib/selfReport'
import { Button, Icon, Notice, NumberStepper, ProblemNotice, TextArea } from '../ui'
import { ConfirmDialog } from '../ui/ConfirmDialog'
import { CaffeineFields } from './CaffeineFields'
import { ChoiceChips } from './ChoiceChips'
import { ClockField } from './ClockField'
import { ExerciseFields } from './ExerciseFields'
import { JournalGroup } from './JournalGroup'
import { TagChips } from './TagChips'

/**
 * The report, and the fact that somebody actually read it.
 *
 * `SelfReport | null` alone cannot say the difference between "this recording has
 * no journal" and "the request for it failed" -- and `Record.tsx` produces the
 * second as a bare `null` **by design** ("a read that failed is worth no more than
 * a quieter screen"). Conflating them opens a blank form over a journal that
 * exists, which is criterion 10's pre-fill failing silently. A host has to state
 * that it loaded something.
 */
export interface LoadedReport {
  loaded: true
  report: SelfReport | null
  provenance: SelfReportProvenance | null
}

interface SelfReportFormProps {
  recordingId: string
  original: LoadedReport
  /** Handed the server's new record, so a host can refresh anything else it shows. */
  onSaved?: (record: RecordingRecord) => void
}

const STRESS_OPTIONS = Array.from({ length: 10 }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
const QUALITY_OPTIONS = (Object.keys(QUALITY_LABELS) as JournalQuality[]).map((value) => ({
  value,
  label: QUALITY_LABELS[value],
}))
/** Caffeine is asked with "Last cup" by `CaffeineFields` now; alcohol is about
 *  yesterday evening and has its own group. See `CaptureJournal.tsx`. */
const SINCE_WAKING: readonly NumericField[] = ['water_glasses']

/**
 * Are two draft fields the same answer?
 *
 * `!==` is wrong for `tags`, and wrong in the direction that matters: two arrays
 * are never reference-equal, so a strict comparison reports a change on every
 * single evaluation. That is how a tag edit came to warn "updated elsewhere" after
 * a 409 even when the re-read was byte-identical.
 */
function sameField(a: SelfReportDraft[DraftField], b: SelfReportDraft[DraftField]): boolean {
  if (Array.isArray(a) && Array.isArray(b)) {
    return a.length === b.length && a.every((value, i) => value === b[i])
  }
  return a === b
}

/** Which fields differ between two drafts. */
const fieldsDiffering = (a: SelfReportDraft, b: SelfReportDraft): DraftField[] =>
  DRAFT_FIELDS.filter((field) => !sameField(a[field], b[field]))

export function SelfReportForm({ recordingId, original, onSaved }: SelfReportFormProps) {
  /** The diff baseline: what the server last told us, never a re-serialised form. */
  const [baseline, setBaseline] = useState<SelfReport | null>(original.report)
  const [provenance, setProvenance] = useState<SelfReportProvenance | null>(original.provenance)
  /**
   * THE DRAFT AND `touched` ARE ONE PIECE OF STATE, NOT TWO.
   *
   * They have to move together, and the place that proves it is the save response:
   * it has to keep a field the user typed WHILE the request was in flight, and keep
   * that field touched, or the text sits on screen outside the diff and cannot be
   * saved without retyping it. Deriving one from the other needs both current
   * values, which two separate `useState` updaters cannot see -- and reaching for a
   * ref to bridge them is what `react-hooks/refs` forbids, correctly.
   */
  const [form, setForm] = useState<{ draft: SelfReportDraft; touched: ReadonlySet<DraftField> }>(
    () => ({ draft: draftFromReport(original.report), touched: new Set() }),
  )
  const { draft, touched } = form
  const [open, setOpen] = useState(false)
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<{ cause: unknown; locked: boolean; text?: string } | null>(null)
  const [moved, setMoved] = useState(false)
  /**
   * The landing for the retry inside `ProblemNotice` below, which unmounts when
   * `failure` clears and takes the focused "Try again" with it.
   *
   * ── THERE ARE TWO TARGETS, AND THE 409 PATH IS THE REASON ────────────────
   *
   * Usually the outcome is "Journal saved." in `.journal__status`, which is
   * already mounted and already empty when idle. But the re-read path at the
   * `setMoved(true)` site batches `setMoved(true)` with `setFailure(null)`, so
   * the commit that unmounts the notice ALSO mounts the "updated elsewhere"
   * warning -- and that is what says what happened on that path, while
   * `.journal__status` is still empty because nothing was saved. Landing on the
   * empty span there would put a keyboard user on silence.
   *
   * Exactly one of the two holds the ref, because the warning renders only when
   * `moved` and the span gives the ref up in that case.
   */
  const [landOn, armLanding] = useRetryLanding(failure !== null)
  /**
   * "Journal saved." -- cleared by `set`, and nowhere else on purpose.
   *
   * It looks like `send` should clear it too, so a success followed by a failure does
   * not show the reassurance beside the error. It cannot happen: a save requires a
   * non-empty diff, `applyResponse` clears `touched`, and only `set` puts anything
   * back into it -- and `set` clears this. Every path to a second save therefore
   * passes through the one place that resets it.
   */
  const [saved, setSaved] = useState(false)
  const [confirming, setConfirming] = useState(false)
  /** The body as sent, so a transport retry resends it rather than rebuilding. */
  const [pending, setPending] = useState<{ patch: ReturnType<typeof buildPatch>['patch']; draft: SelfReportDraft } | null>(null)

  /**
   * The draft, readable synchronously after an await.
   *
   * A save is a network round trip and the keyboard is not taken away for it, so
   * the response handler has to know what the draft looks like NOW rather than
   * what it looked like when the request went out. `Record.tsx` keeps refs for the
   * same reason and says so: state read at the top of an async handler is the
   * value from the render that started it.
   */
  const set = useCallback(<K extends DraftField>(field: K, value: SelfReportDraft[K]) => {
    setForm((current) => ({
      draft: { ...current.draft, [field]: value },
      touched: current.touched.has(field) ? current.touched : new Set(current.touched).add(field),
    }))
    setSaved(false)
  }, [])

  /**
   * A NEW RECORDING IS A NEW FORM. A NEW REPORT FOR THE SAME ONE IS NOT.
   *
   * Done as a render-time adjustment rather than in an effect, for the reason
   * `useResource` gives: an effect runs a frame later, which is long enough to
   * paint one recording's journal under another's heading. Trap 6.
   *
   * ── AND IT KEYS ON THE ID ALONE, WHICH IS NOT AN OVERSIGHT ─────────────────
   *
   * Keying on `original.report` as well looks stricter and is wrong twice. The
   * host is handed the saved record through `onSaved`, so it re-renders with a new
   * report immediately after every successful save -- which would fire this reset
   * against our OWN response, wiping the confirmation the user is reading and
   * undoing the in-flight-keystroke handling in `applyResponse`. And a background
   * refresh for the same recording must not clobber a half-written journal: §3.3
   * says an unsaved form is retained, and this is where that is true or not.
   */
  const [seed, setSeed] = useState(recordingId)
  if (seed !== recordingId) {
    setSeed(recordingId)
    setBaseline(original.report)
    setProvenance(original.provenance)
    setForm({ draft: draftFromReport(original.report), touched: new Set() })
    setFailure(null)
    setSaved(false)
    setPending(null)
    setMoved(false)
  }

  const errors = draftErrors(draft, touched)
  const invalid = Object.keys(errors) as DraftField[]
  const built = buildPatch(baseline, draft, touched)
  const canSave = built.changed.length > 0 && invalid.length === 0 && !saving

  const tagsBlocked = hasUnknownTags(baseline)
  const rawEntries = untypedRawEntries(baseline)
  // `from_journal` lifts typed fields only when `schema_version` is exactly 1, so
  // a journal written to another version arrives with every answer in `raw` and
  // nothing typed. Opening blank over that is the failure criterion 10 names.
  /**
   * A journal whose answers are all in `raw` and none in the typed view.
   *
   * `from_journal` lifts typed fields only when `schema_version` is exactly the
   * integer 1, so a journal written to any other version arrives this way -- with
   * `origin: captured`, because ingest really did select the file. Opening blank
   * over it is criterion 10's pre-fill failing silently.
   *
   * Derived from the BASELINE alone, never from the draft. Keyed on the draft, the
   * explanation vanishes at the first keystroke -- which is the moment "anything you
   * enter here is added alongside them" begins to matter.
   */
  const seeded = draftFromReport(baseline)
  const rawOnly = baseline !== null
    && rawEntries.length > 0
    && DRAFT_FIELDS.every((field) => {
      const value = seeded[field]
      return value === null || value === '' || (Array.isArray(value) && value.length === 0)
    })

  const applyResponse = useCallback((record: RecordingRecord, submitted: SelfReportDraft) => {
    const report = record.self_report ?? null
    setBaseline(report)
    setProvenance(record.self_report_provenance ?? null)
    setPending(null)
    setFailure(null)
    setMoved(false)
    setSaved(true)
    /**
     * Re-seed from the server, EXCEPT where the user has typed since submitting.
     *
     * ── AND KEEP THOSE FIELDS TOUCHED, WHICH IS THE HALF THAT BITES ──────────
     *
     * A save takes a network round trip and this form is a keyboard away from the
     * user the whole time. Clearing `touched` wholesale strands anything typed
     * during the flight: the draft keeps the newer text, the baseline is the
     * server's older answer, and with the field no longer touched the diff is
     * empty -- so Save greys out and that text can never be sent. The user would
     * have to retype it to make it savable, having watched it survive on screen.
     */
    const fresh = draftFromReport(report)
    setForm((current) => {
      // Anything the user typed between the request going out and the answer coming
      // back is kept, AND kept touched -- otherwise the text stays on screen while
      // the diff forgets it, and it cannot be saved without being retyped.
      const typedSince = fieldsDiffering(current.draft, submitted)
      const next = { ...fresh }
      for (const field of typedSince) Object.assign(next, { [field]: current.draft[field] })
      return { draft: next, touched: new Set(typedSince) }
    })
    onSaved?.(record)
  }, [onSaved])

  const send = useCallback(
    async (patch: ReturnType<typeof buildPatch>['patch'], submitted: SelfReportDraft) => {
      setSaving(true)
      try {
        assertWireSafe(patch)
        const record = await patchSelfReport(recordingId, patch)
        applyResponse(record, submitted)
      } catch (err) {
        // §3.4: a failed journal save is a retryable inline error and never a
        // blocked or discarded recording. The draft is untouched here.
        const locked = err instanceof ApiError && err.code === 'recording.baseline_locked'
        // The error itself, not a sentence extracted from it. `err.code` was
        // already being read one line above to choose the copy, and then thrown
        // away -- so the screen branched on the code and never showed it, which
        // is exactly what §7.1 asks for.
        setFailure({ cause: err, locked })
        setOpen(true)
      } finally {
        setSaving(false)
      }
    },
    [applyResponse, recordingId],
  )

  const save = useCallback(() => {
    // An empty patch is NOT a no-op: `_write_provenance` runs on every PATCH, so
    // it would stamp `edited: true` for an edit nobody made.
    //
    // DELIBERATELY UNREACHABLE, and recorded as a surviving mutant rather than
    // chased with a contrived test. `canSave` below reads the same two values, so the
    // button is already disabled in both cases. It stays because the two conditions
    // are one rule enforced in two places -- the button so the user is told, this so
    // the rule holds even if a future host wires its own trigger.
    //
    // Two other guards in this file survive for exactly the same reason and are not
    // defects: `assertWireSafe` in `send`, whose failure modes the shapes above it
    // already prevent, and `if (saving) return` in `retry`, which the disabled button
    // beside it makes unreachable.
    if (built.changed.length === 0 || invalid.length > 0) return
    if (built.clearsEverything && !confirming) {
      setConfirming(true)
      return
    }
    setConfirming(false)
    setPending({ patch: built.patch, draft })
    void send(built.patch, draft)
  }, [built, confirming, draft, invalid.length, send])

  const retry = useCallback(() => {
    // A second tap while the first retry is still in flight fires a second PATCH.
    // The button is disabled too; this is the half that cannot be raced.
    if (saving) return

    /**
     * A CORRECTION MADE AFTER THE FAILURE HAS TO GO WITH THE RETRY.
     *
     * The snapshot exists so a transport retry does not rebuild and re-stamp
     * `answered_at`. But the failure notice leaves the form editable on purpose --
     * §3.3 keeps the draft precisely so it can be fixed -- and the natural thing to
     * do after "that did not reach the server" is to change something and press
     * Try again. Resending the snapshot then silently discards the correction and
     * reports success, which is worse than the original failure.
     *
     * So: unchanged draft, resend the snapshot byte-identical. Changed draft, this
     * is a new attempt and is built from what is on screen now.
     */
    const corrected = pending !== null && fieldsDiffering(draft, pending.draft).length > 0
    if (corrected) {
      const rebuilt = buildPatch(baseline, draft, touched)
      if (rebuilt.changed.length === 0) {
        setFailure(null)
        return
      }
      setPending({ patch: rebuilt.patch, draft })
      void send(rebuilt.patch, draft)
      return
    }

    if (failure?.locked) {
      // A 409 means another writer for this user was demonstrably active, so the
      // baseline may have moved. Re-read it rather than resending blind: a patch
      // carrying an explicit `null` would otherwise land a clear on somebody
      // else's fresh value.
      void (async () => {
        setSaving(true)
        try {
          const record = await getRecording(recordingId)
          const fresh = record.self_report ?? null
          const before = draftFromReport(baseline)
          const after = draftFromReport(fresh)
          const changedUnderneath = [...touched].some(
            (field) => !sameField(after[field], before[field]),
          )
          setBaseline(fresh)
          setProvenance(record.self_report_provenance ?? null)
          setFailure(null)
          if (changedUnderneath) {
            setMoved(true)
            return
          }
          const rebuilt = buildPatch(fresh, draft, touched)
          if (rebuilt.changed.length === 0) return
          setPending({ patch: rebuilt.patch, draft })
          await send(rebuilt.patch, draft)
        } catch (err) {
          /**
           * STAYS `locked`, AND THAT IS THE WHOLE POINT.
           *
           * Downgrading it to a transport failure discards the one thing we know --
           * that another writer for this user was active -- while `pending` still
           * holds a body built against the pre-409 baseline. The next "Try again"
           * would then take the transport branch and resend it blind, which is
           * exactly the explicit-null clobber this branch exists to prevent. Keeping
           * the flag means every later retry re-reads first.
           */
          // The re-read's OWN failure is carried through rather than dropped:
          // the sentence is this screen's and the code, if the server sent one,
          // is the server's (§7.1).
          setFailure({
            cause: err,
            locked: true,
            text: 'Could not check whether anything changed while you were writing.',
          })
        } finally {
          setSaving(false)
        }
      })()
      return
    }
    // A transport failure resends the SAME body. Rebuilding calls the clock again,
    // so a first manual report would retry with a different `answered_at`.
    if (pending) void send(pending.patch, pending.draft)
  }, [baseline, draft, failure, pending, recordingId, saving, send, touched])

  const words = provenanceWords(provenance)
  const numeric = (field: NumericField, hint?: string) => (
    <NumberStepper
      key={field}
      label={NUMERIC_FIELDS[field].label}
      value={draft[field]}
      onChange={(next) => set(field, next)}
      min={NUMERIC_FIELDS[field].min}
      max={'max' in NUMERIC_FIELDS[field] ? (NUMERIC_FIELDS[field] as { max: number }).max : undefined}
      step={NUMERIC_FIELDS[field].step}
      unit={NUMERIC_FIELDS[field].unit}
      hint={hint}
      error={errors[field] ?? null}
    />
  )

  return (
    <details
      className="journal"
      open={open}
      onToggle={(event) => setOpen((event.currentTarget as HTMLDetailsElement).open)}
    >
      <summary className="journal__summary">
        <span className="journal__title">
          {baseline === null ? 'Add this session’s journal' : 'This session’s journal'}
          <span className="journal__optional"> — optional</span>
        </span>
        {words && (
          <span className="journal__provenance">
            {/*
              THE GLYPH IS CHOSEN FROM `provenance.origin`, NEVER FROM THE WORDS.

              `unknown` gets none: a device mark beside "Source not recorded"
              would claim a source the server explicitly did not give, which is
              the only reason that origin exists.
            */}
            {provenance?.origin === 'captured' && <Icon name="device" />}
            {provenance?.origin === 'manual' && <Icon name="pen" />}
            {words.origin}
            {words.edited && <> · {words.edited}</>}
          </span>
        )}
        <Icon name="chev" className="journal__chev" />
      </summary>

      <div className="journal__body">
        <p className="journal__lead">
          Your own account of the day. Nothing here is required, nothing is interpreted, and it
          never changes your result.
        </p>

        {rawOnly && (
          <Notice tone="info">
            This journal was recorded in a format this app cannot edit field by field. Your
            answers are kept exactly as they were recorded, below. Anything you enter here is
            added alongside them.
          </Notice>
        )}
        {moved && (
          <Notice tone="warn" ref={landOn} tabIndex={-1}>
            Some of this journal was updated elsewhere while you were writing. Your answers are
            still here — check them and save again.
          </Notice>
        )}

        {/* Flat groups with their time anchors, not an accordion — see the long
            note in `CaptureJournal.tsx`. The two forms ask one set of questions
            and must ask them the same way, or an answer means two things. */}
        <JournalGroup title="How you feel" anchor="right now">
          <ChoiceChips
            label="Mood"
            options={STATE_OPTIONS}
            value={draft.state}
            onChange={(next) => set('state', next as JournalState | null)}
            hint="Your own words for right now, not a reading from the recording."
          />
          <ChoiceChips
            label="Stress 1-10"
            layout="scale"
            options={STRESS_OPTIONS}
            value={draft.stress_1_10}
            onChange={(next) => set('stress_1_10', next)}
          />
        </JournalGroup>

        <JournalGroup title="Sleep" anchor="last night">
          <div className="journal__group journal__group--grid">
            {numeric('sleep_hours', 'To the nearest quarter hour.')}
            <ChoiceChips
              label="Sleep quality"
              options={QUALITY_OPTIONS}
              value={draft.sleep_quality}
              onChange={(next) => set('sleep_quality', next as JournalQuality | null)}
            />
            <ClockField
              value={draft.woke_at_local}
              onChange={(next) => set('woke_at_local', next)}
              error={errors.woke_at_local ?? null}
            />
          </div>
        </JournalGroup>

        <JournalGroup title="Today, before this recording" anchor="since waking">
          {/*
            JOURNAL-FIX-01 (option A), extended by Sprint 1.9.11. "Exercised: No"
            locks Finished, Intensity and Minutes: empty, they are disabled, so
            "No, lightly, for 20 minutes" cannot be entered; already holding a
            value, they stay on screen with the note below and can only be
            cleared. Nothing is cleared for the user.
          */}
          <ExerciseFields draft={draft} set={set} minutesError={errors.exercise_minutes ?? null} />
          <CaffeineFields draft={draft} set={set} cupsError={errors.caffeine_cups ?? null} />
          <div className="journal__group journal__group--grid journal__group--intake">
            {SINCE_WAKING.map((field) => (
              <div className="journal__intake" key={field}>
                {numeric(field)}
                {draft[field] !== '0' && (
                  /*
                    One tap for zero. `0` is a real answer and is not the same as
                    leaving the field blank, and this is the clearest place to make
                    that difference usable rather than merely documented.
                  */
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => set(field, '0')}
                  >
                    None<span className="visually-hidden"> — {NUMERIC_FIELDS[field].label}</span>
                  </button>
                )}
              </div>
            ))}
          </div>
        </JournalGroup>

        <JournalGroup title="Yesterday evening and last night" anchor="last 24 h">
          <div className="journal__group journal__group--grid journal__group--intake">
            <div className="journal__intake">
              {numeric('alcohol_drinks')}
              {draft.alcohol_drinks !== '0' && (
                <button
                  type="button"
                  className="btn btn--ghost btn--sm"
                  onClick={() => set('alcohol_drinks', '0')}
                >
                  None<span className="visually-hidden"> — {NUMERIC_FIELDS.alcohol_drinks.label}</span>
                </button>
              )}
            </div>
          </div>
          <TagChips
            value={draft.tags}
            onChange={(next) => set('tags', next)}
            disabledReason={
              tagsBlocked
                // Says only what is true. An earlier version claimed the tags were
                // "shown as recorded" -- but an unrecognised tag is dropped by
                // `canonicalTags`, so no chip represents it and the raw block cannot
                // surface it either. It is preserved on the server and invisible here.
                ? 'This journal carries a tag this app does not recognise. Tags cannot be edited here, and are left exactly as recorded.'
                : null
            }
          />
        </JournalGroup>

        <JournalGroup title="Anything notable">
          <TextArea
            label="Notes"
            rows={3}
            value={draft.notes}
            onChange={(event) => set('notes', event.target.value)}
          />
        </JournalGroup>

        {rawEntries.length > 0 && (
          <details className="journal__section journal__raw">
            <summary>
              Also recorded with your session ({rawEntries.length})
              <Icon name="chev" className="journal__chev" />
            </summary>
            <dl className="journal__raw-list">
              {rawEntries.map(([key, value]) => (
                <div className="journal__raw-row" key={key}>
                  <dt>{key}</dt>
                  <dd>{typeof value === 'object' ? JSON.stringify(value) : String(value)}</dd>
                </div>
              ))}
            </dl>
            <p className="journal__note">
              Kept exactly as your device recorded it. Editing this journal does not change these.
            </p>
          </details>
        )}

        {failure && (
          <ProblemNotice error={failure.cause} onRetry={retry} retryBusy={saving} landing={armLanding}>
            {/* BOTH, not either. `failure.text` is set at exactly one site --
                the re-read's own catch -- and that site also sets
                `locked: true`, so a `??` short-circuited the reassurance away
                on the single path that most needs it: a 409 whose re-read then
                failed, which is the most alarming state this form can reach.
                §3.4's framing is "never a blocked or discarded recording", and
                the pre-conversion render emitted both paragraphs
                unconditionally. Nothing asserted it, so nothing caught it. */}
            {failure.text ? `${failure.text} ` : 'Your journal could not be saved. '}
            {failure.locked
              ? 'Nothing was lost — your recording is safe and your answers are still here.'
              : 'Nothing was lost — your answers are still here.'}
          </ProblemNotice>
        )}

        <div className="journal__actions">
          <Button type="button" onClick={save} disabled={!canSave} busy={saving}>
            {baseline === null ? 'Save journal' : 'Save changes'}
          </Button>
          <span
            className="journal__status"
            role="status"
            ref={moved ? undefined : landOn}
            tabIndex={-1}
          >
            {saved ? 'Journal saved.' : ''}
          </span>
        </div>
      </div>

      <ConfirmDialog
        open={confirming}
        title="Clear this whole journal?"
        consequence="Every answer you had recorded for this session will be removed."
        detail="There is no version history for a journal, so this cannot be undone. What your device recorded verbatim is kept."
        confirmLabel="Clear it"
        destructive
        busy={saving}
        onConfirm={save}
        onCancel={() => setConfirming(false)}
      />
    </details>
  )
}
